import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { MapPin, Building2, IndianRupee, ArrowRight, Home } from "lucide-react";
import JsonLd, { buildProjectJsonLd } from "../components/JsonLd";
import { resolveImageUrl } from "../components/ImageUploader";

const TIERS = ["All", "Tier 1", "Tier 2", "Tier 3"];
const PROP_TYPES = ["All", "Apartment", "Villa", "Plot", "Independent Land Parcel"];
const SALE_TYPES = ["All", "New", "Resale"];
const CONFIGS = ["All", "1BHK", "2BHK", "3BHK", "4BHK", "5BHK+"];

const tierClass = {
  "Tier 1": "bg-[#10B981]/15 text-[#10B981] border-[#10B981]/30",
  "Tier 2": "bg-amber-500/15 text-amber-400 border-amber-500/30",
  "Tier 3": "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const fmt = (v) => v >= 10000000 ? `₹${(v/10000000).toFixed(2)} Cr` : v >= 100000 ? `₹${(v/100000).toFixed(1)} L` : `₹${v}`;

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ tier: "All", property_type: "All", sale_type: "All", configuration: "All" });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // project for enquiry

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data);
      } catch { toast.error("Failed to load projects"); }
      finally { setLoading(false); }
    })();
  }, []);

  // Deep-link: /projects?prj=<id> opens that project's enquiry dialog on load
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pid = params.get("prj");
    if (!pid || projects.length === 0) return;
    const found = projects.find((x) => x.id === pid);
    if (found) setSelected(found);
  }, [projects]);

  const openProject = (p) => {
    setSelected(p);
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("prj", p.id);
      window.history.replaceState({}, "", url.toString());
    } catch {}
  };
  const closeProject = () => {
    setSelected(null);
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("prj");
      window.history.replaceState({}, "", url.toString());
    } catch {}
  };

  const filtered = useMemo(() => projects.filter((p) => {
    if (filters.tier !== "All" && p.tier !== filters.tier) return false;
    if (filters.property_type !== "All" && p.property_type !== filters.property_type) return false;
    if (filters.sale_type !== "All" && (p.sale_type || "New") !== filters.sale_type) return false;
    if (filters.configuration !== "All") {
      const cfg = String(p.configuration || "").toUpperCase();
      const want = filters.configuration.toUpperCase().replace("+", "");
      // "5BHK+" matches 5BHK, 6BHK, 7BHK etc; others match substring
      if (filters.configuration === "5BHK+") {
        if (!/[5-9]BHK/.test(cfg)) return false;
      } else if (!cfg.includes(want)) return false;
    }
    return true;
  }), [projects, filters]);

  return (
    <div>
      <section className="relative border-b border-[#334155] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="mx-auto max-w-[1400px] px-6 py-20 relative">
          <div className="text-xs uppercase tracking-[0.2em] text-[#10B981]">New Launches</div>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter max-w-3xl leading-[1.05]">
            Residential projects from Tier 1, 2 & 3 <span className="text-[#10B981]">builders</span>.
          </h1>
          <p className="mt-5 text-slate-400 max-w-2xl">Browse curated launches, filter by builder tier and property type, and reach out to the sales desk directly.</p>
        </div>
      </section>

      <div className="mx-auto max-w-[1400px] px-6 py-10">
        <div className="flex items-center flex-wrap gap-4">
          <div className="min-w-[160px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Builder Tier</div>
            <Select value={filters.tier} onValueChange={(v) => setFilters({ ...filters, tier: v })}>
              <SelectTrigger data-testid="filter-project-tier" className="bg-[#1E293B] border-[#334155] text-white h-10 w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {TIERS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Property Type</div>
            <Select value={filters.property_type} onValueChange={(v) => setFilters({ ...filters, property_type: v })}>
              <SelectTrigger data-testid="filter-project-property" className="bg-[#1E293B] border-[#334155] text-white h-10 w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {PROP_TYPES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Configuration</div>
            <Select value={filters.configuration} onValueChange={(v) => setFilters({ ...filters, configuration: v })}>
              <SelectTrigger data-testid="filter-project-config" className="bg-[#1E293B] border-[#334155] text-white h-10 w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                {CONFIGS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">Sale Type</div>
            <div className="inline-flex rounded-full border border-[#334155] bg-[#1E293B] p-1" data-testid="filter-sale-type">
              {SALE_TYPES.map((s) => (
                <button
                  key={s}
                  type="button"
                  data-testid={`filter-sale-type-${s.toLowerCase().replace("/", "")}`}
                  onClick={() => setFilters({ ...filters, sale_type: s })}
                  className={`px-3 py-1.5 text-xs rounded-full transition-colors ${filters.sale_type === s ? "bg-[#10B981] text-white" : "text-slate-400 hover:text-white"}`}
                >{s === "All" ? "All" : s}</button>
              ))}
            </div>
          </div>
          <div className="ml-auto text-xs text-slate-500">{loading ? "Loading…" : `${filtered.length} projects`}</div>
        </div>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p, i) => (
            <Card key={p.id} data-testid="project-card" style={{ animationDelay: `${i * 40}ms` }}
              className="fade-up group overflow-hidden bg-[#1E293B] border-[#334155] hover:border-[#10B981] transition-colors">
              {p.image_url && (
                <div className="h-48 w-full overflow-hidden">
                  <img alt={p.project_name} src={resolveImageUrl(p.image_url)}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center justify-between gap-3">
                  <Badge className={tierClass[p.tier]}>{p.tier}</Badge>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-500">{p.property_type}</span>
                </div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500">{p.builder_name}</div>
                <h3 className="mt-1 font-display text-xl font-semibold tracking-tight leading-tight">{p.project_name}</h3>
                <div className="mt-2 text-sm text-slate-400 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{p.location}</div>
                {p.description && <p className="mt-3 text-sm text-slate-400 clamp-2 leading-relaxed">{p.description}</p>}
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-[#10B981] font-display font-semibold text-lg flex items-center"><IndianRupee className="h-4 w-4" />{fmt(p.price_min).replace("₹","")} <span className="text-slate-500 mx-1 text-sm">–</span> {fmt(p.price_max).replace("₹","")}</div>
                </div>
                <Button data-testid="enquire-btn" onClick={() => openProject(p)} className="mt-5 w-full rounded-full bg-[#10B981] hover:bg-[#059669] text-white">
                  Enquire now <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
        {!loading && filtered.length === 0 && (
          <div className="text-center text-slate-500 py-16 text-sm">No projects match your filters.</div>
        )}
      </div>

      <EnquiryDialog project={selected} onClose={closeProject} projects={projects} />

      {/* JSON-LD RealEstateListing for the currently-open project */}
      {selected && (
        <JsonLd
          keyId="acrepulse-project-ld"
          data={buildProjectJsonLd(selected, { url: typeof window !== "undefined" ? window.location.href : undefined })}
        />
      )}
    </div>
  );
}

function EnquiryDialog({ project, onClose, projects }) {
  const [form, setForm] = useState({
    project_id: "", property_type: "", custom_property: "",
    visitor_name: "", phone: "", email: "", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [useCustom, setUseCustom] = useState(false);

  useEffect(() => {
    if (project) {
      setForm((f) => ({ ...f, project_id: project.id, property_type: project.property_type }));
      setUseCustom(false);
    }
  }, [project]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const selectedProj = projects.find((p) => p.id === form.project_id) || project;
      await api.post("/enquiries", {
        project_id: form.project_id,
        project_name: selectedProj?.project_name || "",
        property_type: useCustom ? form.custom_property : form.property_type,
        visitor_name: form.visitor_name,
        phone: form.phone,
        email: form.email,
        message: form.message,
        status: "new",
      });
      toast.success("Enquiry submitted — our team will reach out shortly.");
      onClose();
      setForm({ project_id: "", property_type: "", custom_property: "", visitor_name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enquiry failed");
    } finally { setBusy(false); }
  };

  if (!project) return null;

  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1E293B] border-[#334155] text-white max-w-lg" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Enquire about {project.project_name}</DialogTitle>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">{project.builder_name} · {project.tier}</div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Project</Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="enq-project" className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1E293B] border-[#334155] text-white max-h-72">
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.project_name} · {p.builder_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Property Type</Label>
            {!useCustom ? (
              <div className="mt-2 flex gap-2">
                <Select value={form.property_type} onValueChange={(v) => setForm({ ...form, property_type: v })}>
                  <SelectTrigger data-testid="enq-property-type" className="bg-[#0F172A] border-[#334155] text-white h-11 flex-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-[#334155] text-white">
                    {["Independent Land Parcel", "Plot", "Apartment", "Villa"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="enq-property-custom-toggle" type="button" variant="outline" className="border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white" onClick={() => setUseCustom(true)}>Other</Button>
              </div>
            ) : (
              <div className="mt-2 flex gap-2">
                <Input data-testid="enq-property-custom" value={form.custom_property} onChange={set("custom_property")} placeholder="Type property type..." className="bg-[#0F172A] border-[#334155] text-white h-11 flex-1" />
                <Button type="button" variant="outline" className="border-[#334155] bg-transparent text-white hover:bg-[#0F172A] hover:text-white" onClick={() => setUseCustom(false)}>Pick</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Name</Label>
              <Input data-testid="enq-name" required value={form.visitor_name} onChange={set("visitor_name")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Phone</Label>
              <Input data-testid="enq-phone" required value={form.phone} onChange={set("phone")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Email</Label>
            <Input data-testid="enq-email" required type="email" value={form.email} onChange={set("email")} className="mt-2 bg-[#0F172A] border-[#334155] text-white h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Message (optional)</Label>
            <Textarea data-testid="enq-message" value={form.message} onChange={set("message")} className="mt-2 bg-[#0F172A] border-[#334155] text-white min-h-[80px]" />
          </div>
          <Button data-testid="enq-submit" type="submit" disabled={busy} className="w-full rounded-full h-11 bg-[#10B981] hover:bg-[#059669] text-white">
            {busy ? "Submitting…" : "Submit enquiry"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
